import React, { useEffect, useLayoutEffect, lazy, Suspense, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import logo from '../../sidebar-logo.png';
import mixpanel from 'mixpanel-browser';
import {
  Extension as ExtensionIcon,
  PhoneIphone as PhoneIphoneIcon,
  Computer as ComputerIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  PlayArrow as PlayArrowIcon,
  Devices as DevicesIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { setLandingPage } from '../../utils/trackingParams';
import { logConversion } from '../../firebase';
import userStore from '../../Stores/User';
import { isPaidProUser, hasPaidPlan } from '../../constants/plans';
import './landingA.scss';

const Testimonials = lazy(() => import('./Components/Testimonials'));
const requestIdleCallback = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));

const ProBadge = observer(() => {
  const isProUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id ? isPaidProUser(userStore.user) : false;
  const hasPlan = hasPaidPlan(userStore.user);
  const [showPopover, setShowPopover] = React.useState(false);
  const badgeStyle = {
    background: isProUser ? 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)' : hasPlan ? 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)' : 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)',
    padding: '2px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', position: 'absolute', top: isProUser ? '35px' : '0px', left: '45px',
    display: 'inline-flex', alignItems: 'center', height: '16px', width: isProUser ? '32px' : hasPlan ? '70px' : '95px', justifyContent: 'center',
    cursor: isProUser ? 'default' : 'pointer', textDecoration: 'none', whiteSpace: 'nowrap', color: 'white'
  };
  const popoverStyle = { position: 'absolute', top: '25px', left: '45px', background: 'rgba(0, 0, 0, 0.9)', padding: '8px 12px', borderRadius: '8px',
    fontSize: '12px', width: '200px', color: 'white', zIndex: 1000, pointerEvents: 'none', opacity: showPopover ? 1 : 0,
    transition: 'opacity 0.2s ease-in-out', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)', backdropFilter: 'blur(5px)'
  };
  if (!userStore.user) return null;
  if (isProUser) return <div style={badgeStyle}>PRO</div>;
  if (hasPlan) return (
    <div style={{ position: 'relative' }}>
      <a href={userStore.customerPortalUrl} target="_blank" rel="noreferrer" style={badgeStyle} onMouseEnter={() => setShowPopover(true)} onMouseLeave={() => setShowPopover(false)}>UPGRADE</a>
      <div style={popoverStyle}>Upgrade to pro to get premium voices, other languages, and more!</div>
    </div>
  );
  return (
    <div style={{ position: 'relative' }}>
      <Link to="/onboarding/ChooseVoice" style={badgeStyle} onMouseEnter={() => setShowPopover(true)} onMouseLeave={() => setShowPopover(false)}>START TRIAL</Link>
      <div style={popoverStyle}>Start your 7-day free trial today!</div>
    </div>
  );
});

const LandingIntegrations = observer(() => {
  const cta = "Start listening";
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  useLayoutEffect(() => {
    // Scroll to top immediately before paint
    window.scrollTo(0, 0);
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    requestIdleCallback(() => {
      mixpanel.track('Integrations Landing Page Visited', { page: 'landing_integrations' });
      setLandingPage('landing_integrations');
    });
  }, []);

  const renderSignUpButton = useCallback(() => {
    return (
      <div className="cta-div" style={{ position: 'static', width: 'auto' }}>
        <Link to="/signin?source=nav_signup&redirected_from=integrations" className="cta-button"
          onClick={() => setTimeout(() => { mixpanel.track('Click Sign Up (Nav)', { placement: 'Integrations' }); logConversion('sign_up_click', 'A'); }, 0)}>
          <div style={{ display: 'flex', justifyContent: 'space-around' }}><div><div>{cta}</div></div></div>
        </Link>
      </div>
    );
  }, [cta]);

  const renderCta = useCallback(() => {
    return (
      <div className="cta-div" style={{ position: 'static' }}>
        <Link to="/signin?source=integrations&redirected_from=integrations" className="cta-button"
          onClick={() => setTimeout(() => { mixpanel.track('Click Try For Free', { placement: 'Integrations' }); logConversion('try_for_free_click', 'A'); }, 0)}>
          <div style={{ display: 'flex', justifyContent: 'space-around' }}><div><div>Start Listening Now</div></div></div>
        </Link>
      </div>
    );
  }, []);

  return (
    <div className="App" style={{ contain: 'layout style paint' }}>
      <Helmet>
        <title>Works Everywhere You Do | Audioread Integrations</title>
        <meta name="description" content="Listen anywhere with cross-platform AI reading. Sync across iPhone, Android, and desktop - even turn PDFs into podcasts. Seamless and smart." />
        <meta property="og:title" content="Works Everywhere You Do | Audioread Integrations" />
        <meta property="og:description" content="Listen anywhere with cross-platform AI reading. Sync across iPhone, Android, and desktop - even turn PDFs into podcasts. Seamless and smart." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="https://audioread.com/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Works Everywhere You Do | Au  dioRead Integrations" />
        <meta name="twitter:description" content="Listen anywhere with cross-platform AI reading. Sync across iPhone, Android, and desktop - even turn PDFs into podcasts. Seamless and smart." />
        <meta name="twitter:image" content="https://audioread.com/logo.png" />
      </Helmet>
      <link rel="preload" href={logo} as="image" />
      
      <header className="App-header" style={{ minHeight: '100vh' }}>
        <div className="nav-bar">
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <div style={{ position: "relative" }} className="logo-container">
              <Link to="/"><img className="audiblogs-logo" src={logo} alt="logo" loading="eager" decoding="sync" style={{ contain: 'layout style paint' }} /></Link>
              <ProBadge />
            </div>
          </div>
          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '20px', fontSize: '1.05rem', fontWeight: 'bold' }}>
              <Link className="signin-button" to="/signin?source=nav_signin&redirected_from=integrations">Sign In</Link>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', transform: 'scale(0.75)', marginLeft: '-20px'}}>
              {renderSignUpButton()}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  marginLeft: '5px',
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  flexShrink: 0
                }}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <CloseIcon style={{ fontSize: '28px' }} /> : <MenuIcon style={{ fontSize: '28px' }} />}
              </button>
            </div>
          </div>
          <div className="mobile-nav">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', transform: 'scale(0.75)', paddingBottom: '15px', marginRight: '-10px'}}>
              {renderSignUpButton()}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  marginLeft: '5px',
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  flexShrink: 0
                }}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <CloseIcon style={{ fontSize: '28px' }} /> : <MenuIcon style={{ fontSize: '28px' }} />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Navigation Menu (shared for desktop and mobile) - Outside nav-bar to prevent clipping */}
        {isMenuOpen && (
          <div 
            style={{
              position: 'fixed',
              top: '80px',
              right: '20px',
              backgroundColor: 'rgba(9, 2, 19, 0.95)',
              padding: '20px',
              width: '220px',
              borderRadius: '10px',
              backdropFilter: 'blur(10px)',
              zIndex: 10000,
              boxSizing: 'border-box',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflow: 'visible'
            }}
          >
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Link
                to="/features"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Features from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Features
              </Link>
              <Link
                to="/how-it-works"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click How It Works from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                How It Works
              </Link>
              <Link
                to="/integrations-and-platforms"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Integrations from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Integrations
              </Link>
              <Link
                to="/solutions-and-accessibility"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Solutions from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Solutions
              </Link>
              <Link
                to="/organizations"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Organizations from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Organizations
              </Link>
              <Link
                to="/api"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click API from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                API
              </Link>
            </div>
          </div>
        )}
        <h1 className="bigger-headline" style={{ marginTop: '60px', paddingTop: '0px' }}>Works Everywhere You Do</h1>
        <h2 className="subheadline">Audioread is a cross-platform AI reader built to fit seamlessly into your digital routine. Whether you're using a browser, smartphone, or tablet, Audioread integrates smoothly, converting your content into natural audio with minimal effort.</h2>
        <p className="details" style={{ marginTop: '20px' }}>If you're on the move, it's the perfect way to keep learning, working, or multitasking hands-free.</p>
      </header>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <ExtensionIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Text-to-Speech App Integration Made Simple</h1>
        </div>
        <p className="details" style={{ marginBottom: '30px', maxWidth: '800px', margin: '0 auto 30px' }}>With one-click text-to-speech app integration, Audioread lets you convert content directly from:</p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ExtensionIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div><strong style={{ background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Browser Extensions:</strong> Chrome, Edge, and Brave support highlighting, article saving, and quick conversion.</div>
          </li>
          <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PhoneIphoneIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div><strong style={{ background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Safari Shortcuts:</strong> Use Apple's native Share Sheet on iPhone, iPad, or macOS to convert articles directly from Safari.</div>
          </li>
          <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ComputerIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div><strong style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Android PWA:</strong> Add Audioread to your Android home screen. Use it like an app, no install required.</div>
          </li>
          <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmailIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div><strong style={{ background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Email Forwarding:</strong> Simply forward an email to convert it into audio.</div>
          </li>
          <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DescriptionIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div><strong style={{ background: 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Drag & Drop Uploads:</strong> Upload PDFs, web pages or plain text instantly from desktop.</div>
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px', maxWidth: '800px', margin: '20px auto 0' }}>No matter the platform, Audioread adapts.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <DescriptionIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>From PDF to Podcast</h1>
        </div>
        <p className="details">Turn documents into audio you can actually finish. Whether you're revising lecture notes, reviewing reports, or catching up on eBooks, Audioread is your go-to pdf to audio reader.</p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Supports complex documents and multi-page PDFs
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Available on mobile and desktop
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Works with Android and iPhone pdf reader workflows
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px', maxWidth: '800px', margin: '20px auto 0' }}>Audioread makes it easy to take your reading offline and turn it into a playlist, no extra apps needed.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <PlayArrowIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Turn Any Text Into a Podcast</h1>
        </div>
        <p className="details">Every converted item is added to your private podcast feed, ready to stream in:</p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Apple Podcasts
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Spotify
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Pocket Casts
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Overcast
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Google Podcasts
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Podcast Addict
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            and many more
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px', maxWidth: '800px', margin: '20px auto 0' }}>It's your own personal text-to-podcast channel, updating automatically with your converted content.</p>
        <p className="details" style={{ maxWidth: '800px', margin: '10px auto 0' }}>Prefer to listen without apps? Use the built-in web player or download the MP3.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <DevicesIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>One Account, All Devices</h1>
        </div>
        <p className="details" style={{ maxWidth: '800px', margin: '0 auto' }}>Start on your laptop, continue on your phone. Audioread syncs your listening across devices. No need to copy files or manage playlists: your audio is ready wherever you are.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <h1 className="headline" style={{ marginBottom: '30px' }}>What Our Users Say</h1>
        <Suspense fallback={<div style={{ textAlign: 'center', padding: '40px' }}>Loading testimonials...</div>}>
          <Testimonials />
        </Suspense>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <h1 className="headline">Ready to Listen Smarter?</h1>
        <p className="details">
          Explore how Audioread's <strong>text-to-speech features</strong> help you convert, stream, and learn on your terms.
        </p>
        <div style={{ marginTop: '30px' }}>
          {renderCta()}
        </div>
      </div>

      <div className="footer-links" style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/dmca">DMCA Policy</Link>
        <a href="https://audioread.tolt.io/" target="_blank" rel="noopener noreferrer">Affiliate Program</a>
      </div>
      <div style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          
      </div>
      <div style={{ margin: '25px', opacity: 0.7 }}>© {new Date().getFullYear()} Audioread™. All rights reserved. | <a href="mailto:info@audioread.com" target="_blank" rel="noopener noreferrer">
          Contact us at info@audioread.com
        </a></div>
    </div>
  );
});

export default LandingIntegrations;

